import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AttachmentHandler,
    AuthorityConditionDto,
    AuthorityRegisterSummaryDto,
    AuthorityTypeDto,
    CheckExpiryDto,
    CreatePersonnelRegisterEntryCommand,
    CurrentAttachmentVersionDto,
    FEATURE_CURRENCY,
    FlyFreelyError,
    FlyFreelyLoggingService,
    NameId,
    OrganisationAuthorityDto,
    OrganisationAuthorityService,
    PersonService,
    PersonnelRegisterEntryDto,
    PersonnelRegisterExpirySummary,
    PersonsOrganisationDto,
    Register,
    SimpleOrganisationDto,
    UpdatePersonnelRegisterEntryCommand,
    UpdateRegisterSettingsCommand,
    UserService,
    WorkTracker,
    displayableRegisterModeLookup,
    fromLocalDate,
    hasFeatureFlag,
    toLocalDate
} from '@flyfreely-portal-ui/flyfreely';
import {
    FormatDatePipe,
    FormatDateTimePipe,
    FormatPersonPipe
} from '@flyfreely-portal-ui/ui';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import { CurrencyDialoguesService } from 'libs/currency/src/lib/currency-dialogues.service';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    firstValueFrom,
    forkJoin,
    of
} from 'rxjs';
import {
    catchError,
    map as rxMap,
    shareReplay,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    buildCheckContent,
    getCheckResultDetails,
    getPopoverValue,
    mapCheckExpiry,
    validateExistingEntityEndDate,
    validateExistingEntityStartDate,
    validateNewEntityEndDate,
    validateNewEntityStartDate
} from '../helpers';
import {
    CheckResults,
    EditEntryForm,
    NewEntryForm,
    RegisterCheck,
    RegisterEntry,
    SelectableStatusSource,
    UiRegisterConditionsHandler,
    mergeCheckResults
} from '../interfaces';

@Component({
    selector: 'personnel-register-dialogue',
    templateUrl: './personnel-register-dialogue.component.html'
})
export class PersonnelRegisterDialogue implements OnDestroy {
    @Input() authority: OrganisationAuthorityDto;
    @Input() managingOrganisationId: number;
    @Input() organisationId: number;
    @Input() authorityType: AuthorityTypeDto;
    @Input() register: AuthorityRegisterSummaryDto;

    attachmentHandler: AttachmentHandler;
    attachments: CurrentAttachmentVersionDto[];

    candidates: NameId[] = [];
    /**
     * The entries in the register.
     */
    entries: RegisterEntry[] = [];
    entriesBuf$ = new BehaviorSubject<RegisterEntry[]>([]);
    sortList: { [key: string]: any };

    registerConditionsHandler: UiRegisterConditionsHandler;
    personnelRegister: Register<
        CreatePersonnelRegisterEntryCommand,
        UpdatePersonnelRegisterEntryCommand,
        PersonnelRegisterEntryDto
    >;

    displayableRegisterModeLookup = displayableRegisterModeLookup;

    checks: RegisterCheck[] = [];
    checkResults: CheckResults;

    expiry: PersonnelRegisterExpirySummary;
    mappedExpiries: {
        [entryId: number]: { [checkId: number]: CheckExpiryDto };
    } = {};

    availableOrganisations$ = new ReplaySubject<SimpleOrganisationDto[]>();

    /**
     * Organisations the current user can list personnel from (intersection of
     * availableOrganisations and the user's own orgs with PERSON_LIST permission).
     * Used for the add-entry organisation selector only.
     */
    addCandidateOrganisations$: Observable<SimpleOrganisationDto[]>;

    /**
     * The organisation whose personnel are shown as candidates for adding.
     * Defaults to the current organisationId.
     */
    selectedCandidateOrganisationId: number;

    hasProfilePicture = true;
    title = 'Personnel';

    @ViewChild('popover', { static: false })
    popover: PopoverDirective;

    error: string;

    statuses = [
        { name: 'Pending', value: 'PENDING' },
        { name: 'Active', value: 'ACTIVE' },
        { name: 'Suspended', value: 'SUSPENDED' },
        { name: 'Auto', value: 'AUTO' }
    ];

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    // the ID of the entry being edited
    editing: number;
    formGroup: FormGroup<{
        add: FormGroup<NewEntryForm>;
        edit: FormGroup<EditEntryForm>;
    }>;

    expandedSideSheet = false;

    hasCurrency = false;

    hasManagingOrganisation$: Observable<boolean>;

    constructor(
        public modal: BsModalRef<PersonnelRegisterDialogue>,
        modalOptions: ModalOptions,
        private logging: FlyFreelyLoggingService,
        private organisationAuthorityService: OrganisationAuthorityService,
        private formatPersonPipe: FormatPersonPipe,
        private formatDatePipe: FormatDatePipe,
        private formatDateTimePipe: FormatDateTimePipe,
        private personService: PersonService,
        private commonDialoguesService: CommonDialoguesService,
        private personnelDialoguesService: PersonnelDialoguesService,
        private currencyDialoguesService: CurrencyDialoguesService,
        private userService: UserService
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.popover != null && this.popover.isOpen) {
                this.popover.hide();
                return Promise.reject();
            }
            return Promise.resolve();
        };
    }

    ngOnInit() {
        this.selectedCandidateOrganisationId = this.organisationId;

        const userOrgsWithPersonList: SimpleOrganisationDto[] = this.userService
            .findUsersOrganisations()
            .filter(o =>
                o.permissions?.includes(
                    PersonsOrganisationDto.Permissions.PERSON_LIST
                )
            )
            .map(o => ({
                id: o.id,
                name: o.name,
                personalOrganisation: false
            }));

        this.addCandidateOrganisations$ = this.availableOrganisations$.pipe(
            rxMap(available =>
                available.filter(o =>
                    userOrgsWithPersonList.some(u => u.id === o.id)
                )
            ),
            shareReplay(1)
        );

        this.hasManagingOrganisation$ = combineLatest([
            of(this.authority.availableActions.canManage),
            this.availableOrganisations$,
            this.entriesBuf$
        ]).pipe(
            rxMap(
                ([canManage, organisationList, entries]) =>
                    canManage &&
                    (organisationList.length > 1 ||
                        (entries.length > 1 &&
                            entries.some(
                                v =>
                                    v.managingOrganisationId !==
                                    entries[0].managingOrganisationId
                            )))
            ),
            shareReplay(1)
        );

        this.personnelRegister =
            this.organisationAuthorityService.personnelRegister(
                this.authority.id,
                this.register.id,
                this.managingOrganisationId
            );

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.attachmentHandler =
            this.organisationAuthorityService.attachmentHandler(
                this.authority.id,
                this.organisationId
            );

        this.attachmentHandler.attachments$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(attachments => (this.attachments = attachments));

        this.formGroup = new FormGroup({
            add: new FormGroup({
                subjectId: new FormControl([], [Validators.required]),
                startDate: new FormControl(undefined, [Validators.required]),
                endDate: new FormControl(undefined),
                statusSource: new FormControl<SelectableStatusSource>('AUTO', [
                    Validators.required
                ])
            }),
            edit: new FormGroup({
                startDate: new FormControl(undefined, [Validators.required]),
                endDate: new FormControl(undefined),
                statusSource: new FormControl(undefined, [Validators.required])
            })
        });

        this.sortList = {};
        this.fetchData();
        this.registerConditionsHandler = this.getConditionsHandler();

        this.hasCurrency = pipe(
            this.userService.findOrganisationForUser(this.organisationId),
            map(o => hasFeatureFlag(o, FEATURE_CURRENCY)),
            getOrElse(() => false)
        );

        if (this.authority.availableActions.canManage) {
            this.organisationAuthorityService
                .findOrganisationsWithAccess(
                    this.authority.id,
                    this.managingOrganisationId
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: orgs => this.availableOrganisations$.next(orgs),
                    error: error =>
                        this.logging.error(
                            error,
                            'Error fetching available organisations'
                        )
                });
        }
    }

    ngOnDestroy(): void {
        if (this.attachmentHandler != null) {
            this.attachmentHandler.destroy();
        }
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();

        this.availableOrganisations$.complete();
        this.entriesBuf$.complete();
    }

    fetchData() {
        forkJoin([
            this.refreshCandidates(),
            this.refreshExisting(),
            this.refreshExpiries()
        ])
            .pipe(
                switchMap(([candidates, entries, expiries]) => {
                    this.candidates = candidates;
                    this.entries = entries;
                    this.entriesBuf$.next(entries);
                    this.add.controls.startDate.setValidators([
                        Validators.required,
                        validateNewEntityStartDate(
                            this.entries,
                            this.formGroup,
                            'person'
                        )
                    ]);
                    this.add.controls.endDate.setValidators([
                        validateNewEntityEndDate(
                            this.entries,
                            this.formGroup,
                            'person'
                        )
                    ]);
                    return this.refreshChecks();
                }),
                switchMap(checks => {
                    this.checks = checks;
                    return this.refreshExpiries();
                }),
                switchMap(() => this.refreshCheckResults()),
                takeUntil(this.ngUnsubscribe$),
                catchError(error => {
                    this.logging.error(
                        error,
                        'Error fetching personnel register data'
                    );
                    return of({});
                })
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error in fetchData');
                }
            })
            .add(this.workTracker.createTracker());
    }

    refreshRegister() {
        this.organisationAuthorityService
            .findAuthority(this.authority.id, this.managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authority => {
                    this.authority = authority;
                    const register = authority.registers.find(
                        r => r.id === this.register.id
                    );
                    this.register = register;
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error refreshing register details: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    refreshConditions() {
        this.refreshChecks()
            .pipe(
                switchMap(checks => {
                    this.checks = checks;
                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error refreshing conditions');
                }
            })
            .add(this.workTracker.createTracker());
    }

    onConditionsChanged() {
        this.refreshRegister();
        this.refreshConditions();
    }

    openPopover(pop: any) {
        this.popover = pop;
    }

    getConditionsHandler() {
        return {
            findCandidateAuthorityTypes: () =>
                this.organisationAuthorityService.findAvailableAuthorityTypes(
                    this.organisationId
                ),
            findRegisterConditions: () =>
                this.personnelRegister.findRegisterConditions(),
            updateRegisterConditions: (conditions: AuthorityConditionDto[]) =>
                this.personnelRegister.updateRegisterConditions(
                    {
                        managingOrganisationId: this.organisationId,
                        authorityRegisterId: this.register.id,
                        conditions
                    },
                    this.authority.id
                ),
            updateRegisterSettings: (
                registerMode: UpdateRegisterSettingsCommand.RegisterMode
            ) =>
                this.personnelRegister.updateRegisterSettings(
                    {
                        authorityRegisterId: this.register.id,
                        registerMode
                    },
                    this.authority.id
                )
        };
    }

    private toRegisterEntry(c: PersonnelRegisterEntryDto): RegisterEntry {
        return {
            id: c.id,
            startDate: fromLocalDate(c.startDate),
            endDate: fromLocalDate(c.endDate),
            subjectId: c.personId,
            subjectName: this.formatPersonPipe.transform(c.person),
            statusSource: c.statusSource,
            managingOrganisationId: c.managingOrganisationId
        };
    }

    refreshExpiries() {
        return this.organisationAuthorityService
            .personnelRegisterExpiries(
                this.authority.id,
                this.register.id,
                this.managingOrganisationId
            )
            .pipe(
                tap(expiry => {
                    this.expiry = expiry;
                }),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    refreshCandidates() {
        const doneWorking = this.workTracker.createTracker();

        return this.personService
            .findPersonnel(this.selectedCandidateOrganisationId)
            .pipe(
                rxMap(personnel =>
                    personnel.map(p => ({
                        id: p.id,
                        name: this.formatPersonPipe.transform(p)
                    }))
                ),
                tap({ complete: doneWorking }),
                takeUntil(this.ngUnsubscribe$)
            );
    }

    onCandidateOrganisationChanged(orgId: number) {
        this.selectedCandidateOrganisationId = orgId;
        this.refreshCandidates()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: candidates => {
                    this.candidates = candidates;
                    this.add.controls.subjectId.patchValue([]);
                },
                error: error =>
                    this.logging.error(
                        error,
                        'Error refreshing candidates for selected organisation'
                    )
            });
    }

    refreshExisting() {
        return this.personnelRegister.find().pipe(
            rxMap(candidates => candidates.map(c => this.toRegisterEntry(c))),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    refreshChecks() {
        return this.personnelRegister.findRegisterConditions().pipe(
            rxMap(conditions =>
                conditions.conditions
                    .filter(c => c.enabled)
                    .map(c => ({
                        id: c.id,
                        entity: c.entity,
                        entityId: c.entityId,
                        name: c.name,
                        description: c.description,
                        conditionType: c.conditionType
                    }))
            )
        );
    }

    findCheckResult(entry: RegisterEntry, check: RegisterCheck) {
        if (this.checkResults == null || this.checkResults[entry.id] == null) {
            return null;
        }
        const results = this.checkResults[entry.id];
        if (results[check.id] != null) {
            return results[check.id];
        } else if (results[check.entityId] != null) {
            return results[check.entityId];
        } else {
            const key = Object.keys(results)
                .filter(k => k != 'status')
                .find(
                    k => results[k]?.checkConditionType === check.conditionType
                );
            if (key != null) {
                return results[key];
            } else {
                return null;
            }
        }
    }

    refreshCheckResults() {
        if (this.entries == null || this.entries.length === 0) {
            return of({});
        }

        return forkJoin(
            this.entries.map(e =>
                this.personnelRegister.findCheckValues(e.id).pipe(
                    rxMap(results => {
                        return {
                            status: results.status,
                            entryId: e.id,
                            checks: results.checks.map(r => {
                                if (this.mappedExpiries[e.id] != null) {
                                    this.mappedExpiries = {
                                        ...this.mappedExpiries,
                                        [e.id]: {
                                            ...this.mappedExpiries[e.id],
                                            [r.authorityConditionId]:
                                                mapCheckExpiry(
                                                    this.expiry,
                                                    r,
                                                    this.register.id
                                                )
                                        }
                                    };
                                } else {
                                    this.mappedExpiries = {
                                        ...this.mappedExpiries,
                                        [e.id]: {
                                            [r.authorityConditionId]:
                                                mapCheckExpiry(
                                                    this.expiry,
                                                    r,
                                                    this.register.id
                                                )
                                        }
                                    };
                                }
                                return {
                                    checkId:
                                        r.authorityConditionId ??
                                        r.verificationEntityId ??
                                        r.entityId ??
                                        -1,
                                    checkConditionType: r.conditionType,
                                    content: buildCheckContent(
                                        r,
                                        date =>
                                            this.formatDatePipe.transform(date),
                                        timestamp =>
                                            this.formatDateTimePipe.transform(
                                                timestamp
                                            )
                                    ),
                                    popover: getPopoverValue(r),
                                    verificationEntityId:
                                        r.verificationEntityId,
                                    details: getCheckResultDetails(
                                        r,
                                        this.authority.id
                                    ),
                                    activity: r
                                };
                            })
                        };
                    })
                )
            )
        ).pipe(
            rxMap(results => results.reduce(mergeCheckResults, {})),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    onCheckUpdated() {
        this.refreshExpiries()
            .pipe(
                switchMap(() => this.refreshCheckResults()),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error, 'Error updating check results');
                }
            })
            .add(this.workTracker.createTracker());
    }

    findCheckAttachment(checkName: string) {
        return this.attachments.find(a => a.name === checkName) ?? null;
    }

    createConcreteEntry() {
        if (this.add.invalid) {
            this.add.markAllAsTouched();
            this.logging.error(null, 'Not all fields have been completed');
            return;
        }

        const entry = {
            ...this.add.value,
            statusSource: this.authority.availableActions.canManage
                ? this.add.value.statusSource
                : ('NOMINATED' as CreatePersonnelRegisterEntryCommand.StatusSource)
        };

        const selectedPersonIds = Array.isArray(entry.subjectId)
            ? entry.subjectId
            : [entry.subjectId];
        const createObservables = selectedPersonIds.map(personId =>
            this.organisationAuthorityService
                .personnelRegister(this.authority.id, this.register.id)
                .createRegisterEntry({
                    personId: personId,
                    startDate: toLocalDate(entry.startDate),
                    endDate: toLocalDate(entry.endDate),
                    statusSource: entry.statusSource,
                    managingOrganisationId: this.organisationId
                })
                .pipe(
                    tap(() =>
                        this.organisationAuthorityService.notifyUpdated(
                            this.authority.id
                        )
                    ),
                    rxMap(c => this.toRegisterEntry(c))
                )
        );

        forkJoin<(RegisterEntry | null)[]>(
            createObservables.length > 0 ? createObservables : [of(null)]
        )
            .pipe(
                switchMap((results: (RegisterEntry | null)[]) => {
                    const validResults: RegisterEntry[] = results.filter(
                        (r): r is RegisterEntry => r !== null
                    );

                    if (validResults.length > 0) {
                        this.entries.push(...validResults);
                    }

                    this.logging.success(
                        `${validResults.length} new entr${
                            validResults.length === 1 ? 'y' : 'ies'
                        } added to register`
                    );
                    this.add.reset();
                    this.add.controls.subjectId.patchValue([]);
                    this.add.controls.statusSource.patchValue('AUTO');

                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while adding personnel to register: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }

    updateConcreteEntry() {
        const payload = this.formGroup.get('edit').value;

        this.organisationAuthorityService
            .personnelRegister(this.authority.id, this.register.id)
            .updateRegisterEntry(this.editing, {
                startDate: toLocalDate(payload.startDate),
                endDate: toLocalDate(payload.endDate),
                statusSource: payload.statusSource,
                managingOrganisationId: this.organisationId
            })
            .pipe(
                tap(() =>
                    this.organisationAuthorityService.notifyUpdated(
                        this.authority.id
                    )
                ),
                rxMap(c => this.toRegisterEntry(c)),
                switchMap(result => {
                    const index = this.entries.findIndex(
                        e => e.id === this.editing
                    );
                    this.entries.splice(index, 1, result);
                    this.editing = null;
                    this.logging.success('Register entry updated');
                    return this.refreshExpiries().pipe(
                        switchMap(() => this.refreshCheckResults())
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: checkResults => {
                    this.checkResults = checkResults;
                },
                error: error => {
                    this.logging.error(error);
                }
            })
            .add(this.workTracker.createTracker());
    }

    deleteConcreteEntry(entry: RegisterEntry) {
        const deleteRegisterEntry = () => {
            return firstValueFrom(
                this.organisationAuthorityService
                    .personnelRegister(this.authority.id, this.register.id)
                    .deleteRegisterEntry(entry.id, this.organisationId)
                    .pipe(
                        tap(() =>
                            this.organisationAuthorityService.notifyUpdated(
                                this.authority.id
                            )
                        )
                    )
            ).then(
                () => {
                    const index = this.entries.indexOf(entry);
                    this.entries.splice(index, 1);
                    this.logging.success('Register entry deleted');
                },
                error => this.logging.error(error)
            );
        };
        this.commonDialoguesService.showConfirmationDialogue(
            'Delete entry',
            'Are you sure you wish to delete this authority register entry?',
            'Delete',
            deleteRegisterEntry
        );
    }

    changeManagingOrganisation(
        entry: RegisterEntry,
        value: { managingOrganisationId: number }
    ) {
        this.personnelRegister
            .changeRegisterEntryManagingOrganisation(
                entry.id,
                this.authority.id,
                {
                    managingOrganisationId: this.managingOrganisationId,
                    registerEntryManagingOrganisationId:
                        value.managingOrganisationId
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: () => {
                    entry.managingOrganisationId = value.managingOrganisationId;
                    this.logging.success('Managing organisation updated');
                },
                error: error =>
                    this.logging.error(
                        error,
                        'Error updating managing organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    showEntry(entry: RegisterEntry): void {
        this.personnelDialoguesService.showPersonDetailsDialogue(
            entry.subjectId,
            this.organisationId,
            true
        );
    }

    showEditMode(entry: RegisterEntry): void {
        this.editing = entry.id;
        this.edit.setValue({
            startDate: entry.startDate,
            endDate: entry.endDate,
            statusSource: <SelectableStatusSource>entry.statusSource
        });
        this.edit.controls.startDate.setValidators([
            Validators.required,
            validateExistingEntityStartDate(entry.id, this.entries, 'person')
        ]);
        this.edit.controls.endDate.setValidators([
            validateExistingEntityEndDate(entry.id, this.entries, 'person')
        ]);
        entry.editable = true;
    }

    cancelEditMode(entry: RegisterEntry): void {
        entry.editable = false;
        this.editing = null;
    }

    sort(name: string) {
        if (this.sortList[name]) {
            this.sortList[name] = {
                ascending: !this.sortList[name].ascending
            };
        } else {
            this.sortList = {};
            this.sortList[name] = {
                ascending: true
            };
        }
        this.sortTable(name, this.sortList[name].ascending);
    }

    sortTable(name: string, ascending: boolean) {
        if (ascending) {
            this.entryAsc(name);
        } else {
            this.entryDes(name);
        }
    }

    entryAsc(name: string) {
        this.entries.sort((a, b) => {
            if (a[name] > b[name]) {
                return 1;
            } else if (a[name] < b[name]) {
                return -1;
            } else {
                return 0;
            }
        });
    }

    entryDes(name: string) {
        this.entries.sort((a, b) => {
            if (a[name] > b[name]) {
                return -1;
            } else if (a[name] < b[name]) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    filterByStatus(value: string) {
        if (value === 'all') {
            this.entries = this.entriesBuf$.getValue();
        } else {
            this.entries = this.entriesBuf$.getValue().filter(entry => {
                return this.checkResults[entry.id]?.status === value;
            });
        }
    }

    hasError(controlName: string) {
        const control = this.formGroup?.get(controlName);
        return (control.touched || control.dirty) && control.invalid;
    }

    showCurrencyConfiguration() {
        this.currencyDialoguesService
            .showCurrencyConfigurationDialogue(
                this.authorityType.jurisdiction?.id,
                this.register.id,
                this.managingOrganisationId
            )
            .onHide.pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.onConditionsChanged());
    }

    get add() {
        return this.formGroup.controls.add;
    }

    get edit() {
        return this.formGroup.controls.edit;
    }
}
